/** @jsxImportSource @emotion/react */
import { PinInput, PinInputField } from "@chakra-ui/react";
import { css } from "@emotion/react";
import styled from "@emotion/styled";
import { useRouter } from "next/router";
import React, { createRef, useEffect, useRef, useState } from "react";
import ReCAPTCHA from "react-google-recaptcha";
import { useRecoilValue } from "recoil";
import { api } from "../../api/GeneratedApi";
import { PhoneAtom } from "../../atoms/PhoneAtom";
import { CAPTCHA_PUBLIC_KEY, SERVER_URL } from "../../config";
import { localStorageSetItem } from "../../utils/LocalStorage";
import LoadingAbsolute from "../LoadingAbsolute";
import StyledButton from "../StyledButton";

const Wrapper = styled.div`
  max-width: 400px;
  width: 100%;

  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  z-index: 9;
`;

const InputContainer = styled.div`
  width: 100%;
  height: 60px;

  margin-top: 10px;
  margin-bottom: 10px;
`;

const SingleInputContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-evenly;

  > input + input {
    margin-left: 15px;
  }

  @media screen and (max-width: 560px) {
    justify-content: space-between;
  }
`;

const SingleInput = styled.input<{ error: string }>`
  width: 60px;
  height: 70px;
  padding: 8px;
  font-size: 24px;
  appearance: none;
  border: 1px solid ${(props) => (props.error ? "#C00000" : "#cccccc")};
  border-radius: 5px;
  background-color: ${(props) => (props.error ? "#FAEBEB" : "#ffffff")};
  color: #2f2f2f;
  text-align: center;

  :focus {
    outline: none;
  }
`;

const ErrorText = styled.div`
  margin-top: -35px;
  margin-left: 22px;
  color: #c00000;
  size: 14px;

  @media (max-width: 740px) {
    margin-left: 12px;
  }

  @media (max-width: 650px) {
    margin-left: 5px;
  }
`;

const NextButtonContainer = styled.div`
  width: 100%;
  margin: 40px 0 0 0;
`;

const LinkContainer = styled.div`
  width: 400px;
  margin-top: 40px;
  display: flex;
  text-align: center;
  justify-content: space-between;

  @media (max-width: 1152px) {
    margin-top: 32px;
  }

  @media (max-width: 768px) {
    width: 100%;

    margin-top: 24px;
  }
`;

const CustomLink = styled.a`
  font-weight: 600;
  font-size: 16px;
  color: #fb972e;
  cursor: pointer;

  :hover {
    text-decoration: underline;
  }

  @media (max-width: 576px) {
    font-size: 14px;
  }
`;

const StyledPinInputField = styled(PinInputField)`
  width: 60px;
  height: 70px;
  background-color: #ffffff;

  font-size: 24px;

  border: 1px solid ${(props) => (props.error ? "#C00000" : "#cccccc")};

  :focus {
    border-color: ${(props) => (props.error ? "#C00000" : "#fb972e ")};
    box-shadow: 0 0 0 1px ${(props) => (props.error ? "#C00000" : "#fb972e ")};
  }
`;

interface Props {
  onComplete: (user: {
    uid: string;
    authType?: "BERSERKER" | "LEARNISTIC" | "INTERNAL";
  }) => Promise<boolean>;
  setIsNumberInput: () => void;
  type?: string;
}

export default function PasswordInput(props: Props): JSX.Element {
  const { onComplete, type } = props;
  const phone = useRecoilValue(PhoneAtom);

  const router = useRouter();
  const [error, setError] = useState<string>("");
  const [sending, setSending] = useState<boolean>(false);
  const [loading, setLoading] = useState(false);

  const [showCapctha, setShowCaptcha] = useState(false);
  const recaptchaRef = createRef();

  const [password, setPassword] = useState("");
  const firstInput = useRef(null);

  useEffect(() => {
    if (!firstInput.current) return;
    firstInput.current.focus();
  }, [firstInput.current]);

  async function doLogin(password: string) {
    if (sending) return;

    setSending(true);
    const response = await fetch(`${SERVER_URL}/auth/login`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        phone,
        password,
      }),
    });
    const body = await response.json();

    if (body?.data?.login?.userUid && !body?.data?.login?.error) {
      if (body.data.login.authType === "INTERNAL") {
        localStorageSetItem("INTERNAL_TOKEN", body.data.token);
      } else {
        localStorageSetItem("USER_TOKEN", body.data.token);
      }

      await onComplete({
        uid: body.data.login.userUid,
        authType: body.data.login?.authType,
      });
      setSending(false);
      return;
    }

    const responseErrorServer = body?.data?.login?.msg;
    const responseError = body?.rawError?.error.message;
    if (
      responseError === "INVALID_PASSWORD" ||
      responseErrorServer === "INVALID_PASSWORD"
    ) {
      setError("Incorrect password");
    }
    if (
      typeof responseError === "string" &&
      responseError.includes("TOO_MANY_ATTEMPTS_TRY_LATER")
    ) {
      setError("Too many attempts, try again later");
    }

    setSending(false);
  }

  function handlePasswordKeyPressed(value: string) {
    setPassword(value);
    setError("");
    if (value.length === 4) {
      doLogin(value);
    }
  }

  function handleClickLogin() {
    setShowCaptcha(true);
  }

  async function onCaptchaChange(value: string) {
    if (loading) return;

    setLoading(true);

    try {
      if (type === "BERSERKER" || type === "INTERNAL")
        await api.auth.temporaryPasscodeGenerateCreate({
          phone,
          captchaResponse: value,
        });
      else if (type === "LEARNISTIC")
        await api.auth.learnisticTempPasscodeCreate(phone, {
          captchaResponse: value,
        });

      router.replace(`/reset-passcode?type=${type}`);
    } catch (err) {
      console.log("error", err);
    } finally {
      setLoading(false);
    }
  }

  return (
    <>
      <Wrapper>
        {showCapctha ? (
          <ReCAPTCHA
            ref={recaptchaRef}
            sitekey={CAPTCHA_PUBLIC_KEY}
            onChange={onCaptchaChange}
          />
        ) : (
          <>
            <InputContainer>
              <SingleInputContainer>
                <PinInput
                  placeholder=""
                  onChange={handlePasswordKeyPressed}
                  mask
                >
                  <StyledPinInputField ref={firstInput} error={error} />
                  <StyledPinInputField error={!!error} />
                  <StyledPinInputField error={!!error} />
                  <StyledPinInputField error={!!error} />
                </PinInput>
              </SingleInputContainer>
              {error && (
                <NextButtonContainer>
                  <ErrorText>{error}</ErrorText>
                </NextButtonContainer>
              )}
            </InputContainer>
            <StyledButton
              css={css`
                width: 100%;
                font-size: 18px;
                margin-top: 32px;
                border-radius: 46px;
              `}
              disabled={sending}
              onClick={() => doLogin(password)}
            >
              Login
            </StyledButton>
          </>
        )}

        <LinkContainer>
          <CustomLink onClick={() => props.setIsNumberInput()}>
            Go back
          </CustomLink>

          <CustomLink
            onClick={handleClickLogin}
            css={css`
              justify-content: flex-start;
            `}
          >
            Forgot Passcode?
          </CustomLink>
        </LinkContainer>
      </Wrapper>

      {loading && <LoadingAbsolute />}
    </>
  );
}
